import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { IBuild } from "../../../model/model";
import axios from "axios";
import apiUrls from "../../../api";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { RootState } from "../../../common/state/store";

export interface IAskPublicationPayload {
  projectGroupId: number;
  selectedProjectsIds: Array<number | string>;
  selectedPlatforms: string[];
  shouldBeTested: number;
  isNewVersion: boolean;
  callback: () => void;
}

export interface IPublicationParams {
  selectedProjects: number[];
  shouldBeTested: number;
}

export interface IProjectGroupPublicationState {
  currentProjectGroupBuilds: IBuild[];
  newPublication: IPublicationParams;
}

interface IBuilds {
  all: IBuild[];
  inFlight: boolean;
  newPublication: {
    selectedProjectsIds: string[];
    shouldBeTested: 0 | 1;
    isNewVersion: boolean;
    selectedPlatforms: string[];
  };
}

const initialState: IBuilds = {
  all: [],
  inFlight: false,
  newPublication: {
    selectedProjectsIds: [],
    shouldBeTested: 0,
    isNewVersion: false,
    selectedPlatforms: [],
  },
};

export const SLIM_BUILDS_URL =
  `${apiUrls.builds}?fields[0]=id&fields[1]=name&fields[2]=version&fields[3]=status` +
  "&fields[4]=target_platform&fields[5]=scorm_version&fields[6]=package_name" +
  "&fields[7]=updatedAt&fields[8]=publishing_date_ts&fields[9]=request_date_ts" +
  "&fields[10]=s3_url&populate[project_group][fields][0]=id&populate[project_group][fields][1]=name";

export const fetchBuilds = createAsyncThunk(
  "builds/fetchBuilds",
  // eslint-disable-next-line no-empty-pattern
  async ({}: IAppLoaderAction) => {
    return await axios
      .get<IBuild[]>(SLIM_BUILDS_URL)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        console.error(error);
        return undefined;
      });
  },
  {
    condition: (_arg, { getState }) => {
      const state = getState() as RootState;
      return !state.builds.inFlight;
    },
  },
);

export const fetchCurrentProjectGroupBuilds = createAsyncThunk(
  "builds/fetchCurrentProjectGroupBuilds",
  async ({
    projectGroupId,
  }: {
    projectGroupId: number;
  } & IAppLoaderAction) => {
    const url =
      `${apiUrls.builds}?filters[project_group][id]=${projectGroupId}` +
      "&populate[project_group][fields][0]=id&populate[project_group][fields][1]=name";
    return await axios
      .get<IBuild[]>(url)
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        console.error(e);
      });
  },
);

export const askPublication = createAsyncThunk(
  "project/askPublication",
  async ({
    projectGroupId,
    selectedProjectsIds,
    selectedPlatforms,
    shouldBeTested,
    isNewVersion,
    callback,
  }: IAskPublicationPayload & IAppLoaderAction) => {
    try {
      return await axios
        .post<IBuild[]>(`${apiUrls.projects.askPublication}`, {
          projectGroupId,
          selectedProjectsIds,
          selectedPlatforms,
          shouldBeTested,
          isNewVersion,
        })
        .then((res) => {
          callback();
          return { data: res.data };
        });
    } catch (e) {
      console.error(e);
      return { data: [] };
    }
  },
);

export const retryPublication = createAsyncThunk(
  "builds/retryPublication",
  async ({
    buildId,
  }: {
    buildId: number;
  } & IAppLoaderAction) => {
    return await axios
      .post<IBuild>(`${apiUrls.projects.retryPublication}`, { buildId })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        console.error(e);
      });
  },
);

export const buildsSlice = createSlice({
  name: "builds",
  initialState,
  reducers: {
    setPublicationParams: (
      state: IBuilds,
      action: PayloadAction<{ selectedProjectsIds?: string[]; shouldBeTested: boolean }>,
    ) => {
      if (action.payload?.selectedProjectsIds) {
        state.newPublication.selectedProjectsIds = action.payload.selectedProjectsIds;
      }
      state.newPublication.shouldBeTested = action.payload.shouldBeTested ? 1 : 0;
    },
    setIsPublicationNewVersion: (state: IBuilds, action: PayloadAction<boolean>) => {
      state.newPublication.isNewVersion = action.payload;
    },
    setSelectedPlatforms: (state: IBuilds, action: PayloadAction<string[]>) => {
      state.newPublication.selectedPlatforms = action.payload;
    },
    setSelectedProjectsIds: (state: IBuilds, action: PayloadAction<string[]>) => {
      state.newPublication.selectedProjectsIds = action.payload;
    },
    resetNewPublicationState: (state: IBuilds) => {
      state.newPublication = { ...initialState.newPublication };
    },
    clearBuilds: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBuilds.pending, (state: IBuilds) => {
      state.inFlight = true;
    });
    builder.addCase(fetchBuilds.fulfilled, (state: IBuilds, { payload }) => {
      state.inFlight = false;
      const slimBuilds = payload ?? [];
      const hydratedById = new Map(state.all.map((b: IBuild) => [Number(b.id), b]));
      state.all = slimBuilds.map((slim: IBuild) => ({
        ...(hydratedById.get(Number(slim.id)) ?? {}),
        ...slim,
      }));
    });
    builder.addCase(fetchBuilds.rejected, (state: IBuilds) => {
      state.inFlight = false;
    });
    builder.addCase(askPublication.fulfilled, (state, { payload }) => {
      const newBuilds = payload.data;
      const updatedBuildsList = [...state.all, ...newBuilds];
      state.all = updatedBuildsList;
    });
    builder.addCase(retryPublication.fulfilled, (state, { payload }) => {
      if (!payload) return;
      const buildsIR = [...state.all].map((build: IBuild) => {
        if (Number(build.id) === Number(payload.id)) {
          return payload;
        }
        return build;
      });
      state.all = buildsIR;
    });
    builder.addCase(fetchCurrentProjectGroupBuilds.fulfilled, (state, { payload }) => {
      if (!payload) return;
      payload.forEach((elt) => {
        const ind = state.all.findIndex((oldBuild) => Number(oldBuild.id) === Number(elt.id));
        if (ind >= 0) {
          state.all[ind] = elt;
        } else {
          state.all.push(elt);
        }
      });
      state.all = [...state.all];
    });
  },
});

export const {
  setPublicationParams,
  setIsPublicationNewVersion,
  setSelectedPlatforms,
  setSelectedProjectsIds,
  resetNewPublicationState,
  clearBuilds,
} = buildsSlice.actions;

export const buildsReducer = buildsSlice.reducer;

export const getBuilds = (state: RootState) => state.builds.all;
export const getNewPublicationSettings = (state: RootState) => state.builds.newPublication;

export const getCurrentProjectGroupBuilds = (state: RootState) =>
  state.builds.all.filter((b: IBuild) => b.project_group?.id === state.projectGroups.current.id);

export const getLatestBuildsOfProjectGroup = (projectGroupId: number) => (state: RootState) => {
  let latestVersion = 0;
  const relevantBuilds = state.builds.all.filter((b: IBuild) => {
    const matchingProjectGroup = Number(b.project_group?.id) === projectGroupId;
    if (matchingProjectGroup) {
      if (b.version > latestVersion) {
        latestVersion = b.version;
      }
    }
    return matchingProjectGroup;
  });
  return relevantBuilds.filter((b: IBuild) => b.version === latestVersion);
};
